import { useParams, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useState, useMemo } from "react";
import { animeArticles, categories } from "@/mocks/animeData";
import AnimeCard from "@/components/base/AnimeCard";
import Breadcrumb from "@/components/base/Breadcrumb";
import SEOMeta from "@/components/base/SEOMeta";
import SchemaOrg from "@/components/base/SchemaOrg";

export default function Category() {
  const { t } = useTranslation();
  const { slug = "all" } = useParams<{ slug: string }>();
  const [activeSlug, setActiveSlug] = useState(slug);

  const currentCategory = categories.find((c) => c.slug === activeSlug);

  const filteredArticles = useMemo(() => {
    if (activeSlug === "all") return animeArticles;
    return animeArticles.filter((a) => a.categorySlug === activeSlug);
  }, [activeSlug]);

  const pageTitle = currentCategory
    ? `${currentCategory.name} - ${t("seo.categoryTitle")}`
    : `${t("category.allTitle")} - ${t("seo.categoryTitle")}`;

  const pageDesc = currentCategory
    ? `浏览好坏动漫${currentCategory.name}分类的全部评测文章。共${currentCategory.count}篇深度评测，帮助你找到最值得看的${currentCategory.name}动漫。`
    : t("seo.categoryDesc");

  const breadcrumbItems = currentCategory
    ? [{ label: t("breadcrumb.category"), to: "/category/all" }, { label: currentCategory.name }]
    : [{ label: t("category.allTitle") }];

  return (
    <>
      <SEOMeta
        title={pageTitle}
        description={pageDesc}
        keywords={`好坏动漫,${currentCategory ? currentCategory.name : "动漫评测"},动漫推荐,动漫排行榜`}
        canonicalUrl={`https://haohuai.me/category/${activeSlug}`}
      />
      <SchemaOrg
        type="WebPage"
        data={{
          name: pageTitle,
          url: `https://haohuai.me/category/${activeSlug}`,
          description: pageDesc,
          breadcrumb: {
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "首页", item: "https://haohuai.me" },
              {
                "@type": "ListItem",
                position: 2,
                name: currentCategory ? currentCategory.name : "全部评测",
                item: `https://haohuai.me/category/${activeSlug}`,
              },
            ],
          },
        }}
      />

      <main className="min-h-screen pt-16">
        <div className="container-site py-8">
          <Breadcrumb items={breadcrumbItems} />

          <div className="mt-2 mb-8">
            <h1 className="text-2xl md:text-3xl font-bold text-foreground-900 font-heading">
              {currentCategory ? currentCategory.name : t("category.allTitle")}
            </h1>
            <p className="text-sm text-foreground-600 mt-2">{t("category.subTitle")}</p>
          </div>

          <div className="flex items-center gap-2 mb-8 overflow-x-auto pb-2 flex-wrap">
            <Link
              to="/category/all"
              onClick={(e) => {
                e.preventDefault();
                setActiveSlug("all");
                window.history.pushState(null, "", "/category/all");
                window.scrollTo({ top: 0, behavior: "instant" });
              }}
              className={`whitespace-nowrap px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 cursor-pointer ${
                activeSlug === "all"
                  ? "bg-primary-500 text-background-50"
                  : "bg-background-100 text-foreground-600 hover:bg-background-200"
              }`}
            >
              全部
            </Link>
            {categories.map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                onClick={(e) => {
                  e.preventDefault();
                  setActiveSlug(cat.slug);
                  window.history.pushState(null, "", `/category/${cat.slug}`);
                  window.scrollTo({ top: 0, behavior: "instant" });
                }}
                className={`whitespace-nowrap px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 cursor-pointer ${
                  activeSlug === cat.slug
                    ? "bg-primary-500 text-background-50"
                    : "bg-background-100 text-foreground-600 hover:bg-background-200"
                }`}
              >
                {cat.name}
                <span className="ml-1.5 text-xs opacity-70">({cat.count})</span>
              </Link>
            ))}
          </div>

          {filteredArticles.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
              {filteredArticles.map((article) => (
                <AnimeCard key={article.id} article={article} />
              ))}
            </div>
          ) : (
            <div className="text-center py-20">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-background-100 flex items-center justify-center">
                <i className="ri-emotion-sad-line text-2xl text-foreground-400"></i>
              </div>
              <p className="text-foreground-500 text-sm">{t("category.noResults")}</p>
            </div>
          )}
        </div>
      </main>
    </>
  );
}